import styled from "@emotion/styled";
import { useCallback, useMemo, type CSSProperties, type ReactNode } from "react";

import {
  ContextMenu,
  DEFAULT_INPUT_HEIGHT,
  FieldSelect,
  Form,
  FormikForm,
  FormRowField,
  Icon,
  IconButton,
  Label,
  MenuItem,
  PropertiesPanelSection,
  SkiliticsDuotoneIcon,
  SkiliticsIcon,
  Tooltip,
  toSpaceCase,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useEditingCss } from "./EditingCssPropertiesContext";
import { FieldCssInput } from "./FieldCssInput";

const StyledFieldSelect = styled(FieldSelect)`
  height: ${DEFAULT_INPUT_HEIGHT}px;
  line-height: 16px;
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 8px;
  margin-bottom: 8px;
  max-width: 100%;
  min-width: 0;
`;

const UserSelectIcon = styled(SkiliticsDuotoneIcon)`
  --fa-secondary-opacity: 0.15;
`;

type OptionalLayerStyleKey = "cursor" | "opacity" | "transformOrigin" | "zIndex" | "userSelect" | "pointerEvents";

type AlwaysVisibleLayerStyleKey = "rotate" | "borderRadius";

type LayerStyleKey = AlwaysVisibleLayerStyleKey | OptionalLayerStyleKey;

type LayerFormValues = Partial<Record<LayerStyleKey, string>>;

type LayerStyleDefinition = {
  icon: ReactNode;
  tooltipFormatKey: keyof typeof formatStrings;
  alwaysVisible: boolean;
  // Not applicable to the root container, so hidden from its panel and add-menu.
  excludeFromRoot?: boolean;
  defaultCssValue: string;
  renderInput: () => ReactNode;
};

// Optional styles are hidden until added from the "+" menu, so they declare its label.
type OptionalLayerStyleDefinition = LayerStyleDefinition & {
  alwaysVisible: false;
  menuLabelFormatKey: keyof typeof layerStyleMenuFormatStrings;
};

const cursorOptions = [
  "auto",
  "default",
  "pointer",
  "alias",
  "copy",
  "crosshair",
  "grab",
  "grabbing",
  "help",
  "move",
  "no-drop",
  "not-allowed",
  "progress",
  "text",
  "vertical-text",
  "wait",
  "zoom-in",
  "zoom-out",
].map((cursor) => ({
  label: toSpaceCase(cursor),
  value: cursor,
}));

const userSelectOptions = ["auto", "none", "text", "all"].map((userSelect) => ({
  label: toSpaceCase(userSelect),
  value: userSelect,
}));

const pointerEventsOptions = ["auto", "none"].map((pointerEvents) => ({
  label: toSpaceCase(pointerEvents),
  value: pointerEvents,
}));

const layerStyleDefinitions: Record<AlwaysVisibleLayerStyleKey, LayerStyleDefinition> &
  Record<OptionalLayerStyleKey, OptionalLayerStyleDefinition> = {
  rotate: {
    icon: <SkiliticsIcon icon="angle" size={13} />,
    tooltipFormatKey: "rotateTooltip",
    alwaysVisible: true,
    excludeFromRoot: true,
    defaultCssValue: "",
    renderInput: () => <FieldCssInput placeholder="0deg" unit="deg" defaultEmptyValue="none" />,
  },
  borderRadius: {
    icon: <SkiliticsIcon icon="radius-individual" size={13} />,
    tooltipFormatKey: "borderRadiusTooltip",
    alwaysVisible: true,
    excludeFromRoot: true,
    defaultCssValue: "",
    renderInput: () => <FieldCssInput placeholder="0px" />,
  },
  cursor: {
    icon: <SkiliticsIcon icon="layer-cursor" size={15} />,
    tooltipFormatKey: "cursorTooltip",
    alwaysVisible: false,
    menuLabelFormatKey: "addCursor",
    defaultCssValue: "auto",
    renderInput: () => (
      <StyledFieldSelect options={cursorOptions} MenuProps={{ slotProps: { paper: { style: { maxHeight: 280 } } } }} />
    ),
  },
  pointerEvents: {
    icon: <SkiliticsIcon icon="layer-interactions" size={14} />,
    tooltipFormatKey: "pointerEventsTooltip",
    alwaysVisible: false,
    menuLabelFormatKey: "addPointerEvents",
    defaultCssValue: "auto",
    renderInput: () => (
      <StyledFieldSelect
        options={pointerEventsOptions}
        MenuProps={{ slotProps: { paper: { style: { maxHeight: 280 } } } }}
      />
    ),
  },
  opacity: {
    icon: <SkiliticsIcon icon="layer-opacity" size={14} />,
    tooltipFormatKey: "opacityTooltip",
    alwaysVisible: false,
    menuLabelFormatKey: "addOpacity",
    defaultCssValue: "1",
    renderInput: () => <FieldCssInput unit="" defaultEmptyValue="1" step={0.1} />,
  },
  transformOrigin: {
    icon: <Icon icon="location-crosshairs" faPrefix="fas" size={13} />,
    tooltipFormatKey: "transformOriginTooltip",
    alwaysVisible: false,
    excludeFromRoot: true,
    menuLabelFormatKey: "addTransformOrigin",
    defaultCssValue: "center",
    renderInput: () => <FieldCssInput placeholder="center" unit="" defaultEmptyValue="center" />,
  },
  zIndex: {
    icon: <SkiliticsIcon icon="layer-z-order" size={14} />,
    tooltipFormatKey: "zIndexTooltip",
    alwaysVisible: false,
    menuLabelFormatKey: "addZIndex",
    defaultCssValue: "0",
    renderInput: () => <FieldCssInput unit="" defaultEmptyValue="0" />,
  },
  userSelect: {
    icon: <UserSelectIcon icon="layer-text-selection" size={14} />,
    tooltipFormatKey: "userSelectTooltip",
    alwaysVisible: false,
    menuLabelFormatKey: "addUserSelect",
    defaultCssValue: "auto",
    renderInput: () => (
      <StyledFieldSelect
        options={userSelectOptions}
        MenuProps={{ slotProps: { paper: { style: { maxHeight: 280 } } } }}
      />
    ),
  },
};

const layerStyleKeys = Object.keys(layerStyleDefinitions) as LayerStyleKey[];
const isOptionalLayerStyle = (key: LayerStyleKey): key is OptionalLayerStyleKey =>
  !layerStyleDefinitions[key].alwaysVisible;
const alwaysVisibleStyleKeys = layerStyleKeys.filter((key) => !isOptionalLayerStyle(key));
const optionalLayerStyleKeys = layerStyleKeys.filter(isOptionalLayerStyle);

export const LayerProperties = ({ isRootContainer }: { isRootContainer?: boolean }) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { cssProperties, onCssPropertiesChange } = useEditingCss();

  const isAvailableOnContainer = useCallback(
    (key: LayerStyleKey) => !(isRootContainer && layerStyleDefinitions[key].excludeFromRoot),
    [isRootContainer],
  );

  const visibleAlwaysVisibleStyleKeys = useMemo(
    () => alwaysVisibleStyleKeys.filter(isAvailableOnContainer),
    [isAvailableOnContainer],
  );

  const allowedOptionalStyleKeys = useMemo(
    () => optionalLayerStyleKeys.filter(isAvailableOnContainer),
    [isAvailableOnContainer],
  );

  const activeOptionalStyleKeys = useMemo(
    () => allowedOptionalStyleKeys.filter((key) => cssProperties[key] !== undefined),
    [allowedOptionalStyleKeys, cssProperties],
  );

  const availableToAdd = useMemo(
    () => allowedOptionalStyleKeys.filter((key) => cssProperties[key] === undefined),
    [allowedOptionalStyleKeys, cssProperties],
  );

  const initialValues = useMemo<LayerFormValues>(
    () =>
      Object.fromEntries(
        layerStyleKeys.map((key) => [key, cssProperties[key] !== undefined ? String(cssProperties[key]) : undefined]),
      ),
    [cssProperties],
  );

  const handleAddStyle = useCallback(
    (key: OptionalLayerStyleKey) => {
      onCssPropertiesChange({ [key]: layerStyleDefinitions[key].defaultCssValue });
    },
    [onCssPropertiesChange],
  );

  const handleRemoveStyle = useCallback(
    (key: OptionalLayerStyleKey) => {
      onCssPropertiesChange({ [key]: undefined });
    },
    [onCssPropertiesChange],
  );

  const onSubmit = useCallback(
    (values: LayerFormValues) => {
      onCssPropertiesChange(values as CSSProperties);
    },
    [onCssPropertiesChange],
  );

  return (
    <PropertiesPanelSection
      title={formatMessage("sectionHeading")}
      controls={<LayerStyleControls availableToAdd={availableToAdd} onAdd={handleAddStyle} />}
      data-test="layer-styles-section"
      empty={visibleAlwaysVisibleStyleKeys.length === 0 && activeOptionalStyleKeys.length === 0}
    >
      <FormikForm<LayerFormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        submitOnBlur
        dataTest="layer-styles-form"
      >
        <Form $rowSpacing={0} $labelAlignment="inline" $labelWidth={18}>
          {visibleAlwaysVisibleStyleKeys.map((key) => (
            <LayerStyleRow key={key} styleKey={key} />
          ))}
          {activeOptionalStyleKeys.map((key) => (
            <LayerStyleRow key={key} styleKey={key} onRemove={() => handleRemoveStyle(key)} />
          ))}
        </Form>
      </FormikForm>
    </PropertiesPanelSection>
  );
};

type LayerStyleRowProps = {
  styleKey: LayerStyleKey;
  onRemove?: () => void;
};

const LayerStyleRow = ({ styleKey, onRemove }: LayerStyleRowProps) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { icon, tooltipFormatKey, renderInput } = layerStyleDefinitions[styleKey];

  return (
    <Tooltip title={formatMessage(tooltipFormatKey)} placement="left">
      <RowWrapper>
        <FormRowField name={styleKey}>
          <Label>{icon}</Label>
          {renderInput()}
        </FormRowField>
        {onRemove && (
          <IconButton onClick={onRemove} data-test={`remove-${styleKey}-button`}>
            <Icon icon="minus" size={14} />
          </IconButton>
        )}
      </RowWrapper>
    </Tooltip>
  );
};

type LayerStyleControlsProps = {
  availableToAdd: OptionalLayerStyleKey[];
  onAdd: (key: OptionalLayerStyleKey) => void;
};

const LayerStyleControls = ({ availableToAdd, onAdd }: LayerStyleControlsProps) => {
  const formatMessage = useFormatMessage(layerStyleMenuFormatStrings);

  return (
    <ContextMenu
      disabled={availableToAdd.length === 0}
      IconComponent={<Icon icon="plus" />}
      data-test="layer-style-add-context-menu"
    >
      {availableToAdd.map((key) => (
        <MenuItem key={key} onClick={() => onAdd(key)}>
          {formatMessage(layerStyleDefinitions[key].menuLabelFormatKey)}
        </MenuItem>
      ))}
    </ContextMenu>
  );
};

const formatStrings = {
  sectionHeading: { en: "Layer Settings" },
  rotateTooltip: { en: "Rotation" },
  borderRadiusTooltip: { en: "Corner Radius" },
  cursorTooltip: { en: "Mouse Cursor" },
  opacityTooltip: { en: "Opacity" },
  transformOriginTooltip: { en: "Transform Origin" },
  zIndexTooltip: { en: "Z Index" },
  userSelectTooltip: { en: "Text Selection" },
  pointerEventsTooltip: { en: "Pointer Events" },
} satisfies FormatMessages;

const layerStyleMenuFormatStrings = {
  addCursor: { en: "Add Cursor" },
  addOpacity: { en: "Add Opacity" },
  addTransformOrigin: { en: "Add Transform Origin" },
  addZIndex: { en: "Add Z Index" },
  addUserSelect: { en: "Add Text Selection" },
  addPointerEvents: { en: "Add Pointer Events" },
} satisfies FormatMessages;
